import React, {useCallback, useMemo, useState} from "react";
import {DialogContent, DialogTitle, Divider, Typography} from "@mui/material";
import Dialog from "../../base/dialog";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Form, {MuiRFF, Yup} from "../../form";
import Button from "../../base/button/button";
import {useTranslation} from "react-i18next";

/**
 * Maps AI import response items to transfer line payloads keyed by part number.
 */
const mapAiItemsToProducts = (items) => {
    const mergedByPartNo = {};
    (items ?? []).forEach((entry) => {
        const partNo = entry.partNumber?.toUpperCase()?.trim();
        if (!partNo) {
            return;
        }
        const quantity = Number(entry.qty ?? 0);
        if (Number.isNaN(quantity)) {
            return;
        }
        mergedByPartNo[partNo] = (mergedByPartNo[partNo] ?? 0) + quantity;
    });
    return Object.entries(mergedByPartNo).map(([partNo, quantity]) => ({
        partNo,
        quantity,
    }));
};

const ImportTransferPartsWithAIDialog = ({open, close, onImport}) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'inventory-transfer.import-parts-with-ai'});
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        value: Yup.string().nullable().required(ValidateMessages.required).trim(),
    }), []);

    const initialValues = useMemo(() => ({
        value: '',
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Parses pasted content with AI and passes normalized products to the caller.
     */
    const importItemsWithAI = useCallback(async (values) => {
        setImporting(true);
        const response = await Api.importPartsWithAI({
            text: values.value,
        });
        if (!isMounted()) {
            return;
        }
        setImporting(false);
        if (response?.resultFlag) {
            const products = mapAiItemsToProducts(response.data?.items ?? []);
            onImport(products);
            close();
        }
    }, [close, isMounted, onImport]);

    return (
        <Dialog
            open={open ?? false}
            setOpen={close}
            maxWidth={'sm'}
            className={'bound-dialog quick-order-dialog'}
            scroll={'paper'}
            aria-labelledby="import-transfer-parts-with-ai-dialog-title"
        >
            <DialogTitle id={'import-transfer-parts-with-ai-dialog-title'}>
                <p className={'text-lg text-color-1 my-3 font-weight-bold text-center'}>
                    {t('title')}
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent className={'d-flex flex-column p-3'}>
                <Typography variant={'body2'} className={'mb-4'}>
                    {t('description')}
                </Typography>
                <Form
                    onSubmit={importItemsWithAI}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{className: 'd-flex flex-column'}}
                >
                    <MuiRFF.TextField
                        fullWidth
                        multiline
                        minRows={10}
                        maxRows={20}
                        variant={'outlined'}
                        label={t('contentLabel')}
                        placeholder={t('contentPlaceholder')}
                        name={'value'}
                        required={required.value}
                        disabled={importing}
                        className={'mb-3'}
                    />
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        size={'large'}
                        type={'submit'}
                        className={'mt-3'}
                        disabled={importing}
                    >
                        {importing ? t('importing') : t('import')}
                    </Button>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default ImportTransferPartsWithAIDialog;
